import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import type { components } from '@bookingdei/api-client/types';
import { api } from '../../api';
import { useLanguage } from '../../shell/language-context';
import { useResource } from '../../shell/use-resource';
import { useSettings } from '../../shell/settings-context';
import { ErrorState } from '../../shell/state-view';
import type { PlaceOption } from './hero-location-panel';
import { HxCards } from './hx-cards';
import { HxHero } from './hx-hero';
import { HxSprite } from './hx-icon';
import { HxOffers } from './hx-offers';
import { HxPlan } from './hx-plan';
import { HxPopular } from './hx-popular';
import { HxReels } from './hx-reels';
import { HxTrending } from './hx-trending';
import { placeSponsored } from './place-sponsored';
import { useHomeFormat } from './use-home-format';
import { useHxMotion } from './use-hx-motion';
import './hx.css';
import './hx-overrides.css';

// The bands under the card rails are their own chunk (home-below-fold.tsx). The fetch
// starts as soon as this module loads, in parallel with the API calls.
const belowFold = import('./home-below-fold');
const HomeBelowFold = lazy(() => belowFold.then((m) => ({ default: m.HomeBelowFold })));

type Home = components['schemas']['Home'];
type LocationList = components['schemas']['LocationList'];
type LocationNode = components['schemas']['LocationNode'];
type SearchResults = components['schemas']['SearchResults'];
type Trending = components['schemas']['Trending'];
type DirectoryOfferList = components['schemas']['DirectoryOfferList'];

// "Things to do this weekend" draws from these verticals (design weekend grid).
const WEEKEND_CATEGORIES = new Set(['event', 'class', 'turf']);
/** Announced deals the home page loads: the offers rail shows the first twelve, the brand wall draws on all. */
const DEALS_LIMIT = 40;
const RAIL_DEALS = 12;

function toPlaces(tree: LocationNode[], lang: string): PlaceOption[] {
  const all: PlaceOption[] = [];
  const name = (n: LocationNode) => (lang === 'bn' ? n.name_bn : n.name_en) ?? n.name_en;
  const walk = (nodes: LocationNode[]) => {
    for (const n of nodes) {
      if (n.level === 'city') {
        const city: PlaceOption = { id: n.id, slug: n.slug, label: name(n), cityLabel: '', isCity: true };
        all.push(city);
        for (const a of n.children ?? []) {
          if (a.level === 'area') all.push({ id: a.id, slug: a.slug, label: name(a), cityLabel: city.label, isCity: false });
        }
      } else walk(n.children ?? []);
    }
  };
  walk(tree);
  return all;
}

// Home (PRD §7.3 route 1, FR-C1, FR-C19–C22), converted from the Home design
// (claude-design-raw-html/website/Home.dc.html) section by section:
//
//   hero + search + live counter → today's offers → offer reels → "your next plan" band →
//   popular near you → trending → new → this weekend → app → trust stats → for business →
//   brands → browse by area
//
// Every band renders from live data and is absent when it has none; the header and footer
// are the shared shell's. Styles are the design's own, scoped under .hx (hx.css, generated
// by scripts/home-scope-css.mjs).
export function HomeScreen() {
  const { t, lang } = useLanguage();
  const { settings } = useSettings();
  const rootRef = useRef<HTMLDivElement>(null);

  const home = useResource<Home>((signal) => api.GET('/home', { signal, lang }), [lang]);
  const locations = useResource<LocationList>((signal) => api.GET('/registries/locations', { signal, lang }), [lang]);
  const trending = useResource<Trending>((signal) => api.GET('/trending', { signal, lang }), [lang]);
  // Deals businesses announced themselves (FR-A26 v3) share the offers rail with the bookable ones.
  const deals = useResource<DirectoryOfferList>((signal) => api.GET('/offers/directory', { signal, lang, params: { query: { limit: DEALS_LIMIT } } }), [lang]);
  const newest = useResource<SearchResults>((signal) => api.GET('/search', { params: { query: { sort: 'newest', limit: 10 } }, signal, lang }), [lang]);
  const weekend = useResource<SearchResults>((signal) => api.GET('/search', { params: { query: { sort: 'trending', limit: 50 } }, signal, lang }), [lang]);

  const categories = useMemo(() => home.data?.categories ?? [], [home.data]);
  const fmt = useHomeFormat(categories);
  const featured = home.data?.featured ?? [];
  const nearby = home.data?.nearby ?? [];
  const places = useMemo(() => toPlaces(locations.data?.items ?? [], lang), [locations.data, lang]);

  const dealItems = deals.data?.items ?? [];
  const offerCount = (home.data?.offers?.length ?? 0) + Math.min(dealItems.length, RAIL_DEALS);
  const covers = [...featured, ...nearby].filter((b) => b.cover_image?.['400'] ?? b.cover_image?.['800']);
  const photos = covers.map((b) => (b.cover_image?.['400'] ?? b.cover_image?.['800'])!);
  const weekendItems = weekend.data
    ? placeSponsored((weekend.data.items ?? []).filter((b) => WEEKEND_CATEGORIES.has(b.category_id)), 2, 4).slice(0, 4)
    : undefined;
  const appUrl = settings?.web_base_url ? `${settings.web_base_url.replace(/\/$/, '')}/app` : undefined;

  // Re-run the scroll reveal and rail drift once the lazy bands mount, and whenever a band's
  // data arrives and its nodes appear.
  const [belowReady, setBelowReady] = useState(false);
  useEffect(() => {
    let live = true;
    void belowFold.then(() => live && setBelowReady(true));
    return () => {
      live = false;
    };
  }, []);
  useHxMotion(rootRef, [home.data, trending.data, deals.data, newest.data, weekend.data, locations.data, settings, belowReady]);

  if (home.error) return <ErrorState onRetry={home.reload} />;

  return (
    <div className="hx" ref={rootRef}>
      <HxSprite />
      <HxHero categories={categories} places={places} bookingsToday={home.data?.stats?.bookings_today} />
      {offerCount > 0 ? <HxOffers offers={home.data?.offers ?? []} deals={dealItems.slice(0, RAIL_DEALS)} fmt={fmt} /> : null}
      <HxReels />
      <HxPlan photos={photos} categories={covers.map((b) => b.category_id)} />
      <HxPopular nearby={nearby} fmt={fmt} />
      <HxTrending items={(trending.data?.top10 ?? []).map((e) => e.business)} fmt={fmt} />
      <HxCards
        title={t('dc50.car.new')}
        sub={t('hx.new.sub')}
        seeAll={t('hx.new.all')}
        to="/search?sort=newest"
        layout="rail"
        items={newest.data?.items.slice(0, 8)}
        fmt={fmt}
      />
      <HxCards
        title={t('dc50.car.weekend')}
        sub={t('dc50.car.weekend_sub')}
        seeAll={t('hx.see_all')}
        to="/search?sort=trending"
        layout="grid"
        items={weekendItems}
        footnote={t('dc50.popular.rankings_note')}
        fmt={fmt}
      />
      <Suspense fallback={null}>
        <HomeBelowFold
          app={{ appUrl, listings: nearby.filter((b) => !b.sponsored).slice(0, 4), fmt }}
          biz={{ merchantReviews: home.data?.stats?.merchant_reviews }}
          brands={{ deals: dealItems, total: deals.data?.total_estimate }}
          areas={{ locations: locations.data?.items ?? [], categories }}
        />
      </Suspense>
    </div>
  );
}
